import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Cinematic image-led card used across /experiences and similar grids.
 *
 * Always renders as a link — the card uses cursor-pointer + an arrow icon
 * so the visitor expects it to navigate. If a caller forgets to pass an
 * `href`, the card falls back to a non-interactive `<article>` so the
 * affordance no longer lies to the visitor.
 */
export default function ExperienceCard({
  title,
  tagline,
  desc,
  image,
  tag,
  meta,
  testId,
  onClick,
  href,
  external = false,
}) {
  const inner = (
    <>
      <div className="relative aspect-[4/5] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
          style={{ backgroundImage: `url(${image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {tag && (
          <span className="absolute top-4 left-4 bg-amber-400 text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1">
            {tag}
          </span>
        )}
        <div className="absolute top-4 right-4 h-9 w-9 bg-black/60 backdrop-blur-md border border-white/10 group-hover:bg-amber-400 group-hover:border-amber-400 transition-all flex items-center justify-center text-white group-hover:text-black">
          <ArrowUpRight className="h-4 w-4" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-6">
          {tagline && (
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold mb-2">
              {tagline}
            </p>
          )}
          <h3 className="font-display font-bold text-xl sm:text-2xl leading-tight">
            {title}
          </h3>
          {meta && (
            <p className="text-xs text-zinc-400 mt-2 uppercase tracking-[0.18em]">
              {meta}
            </p>
          )}
        </div>
      </div>
      {desc && (
        <div className="p-6 border-t border-white/5">
          <p className="text-sm text-zinc-300 leading-relaxed">{desc}</p>
        </div>
      )}
    </>
  );

  const baseClass =
    "group relative block overflow-hidden border border-white/5 hover:border-amber-400/40 bg-zinc-950 transition-all duration-500";
  const interactiveClass = `${baseClass} cursor-pointer`;

  if (href && external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        onClick={onClick}
        className={interactiveClass}
      >
        {inner}
      </a>
    );
  }
  if (href) {
    return (
      <Link
        to={href}
        data-testid={testId}
        onClick={onClick}
        className={interactiveClass}
      >
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={`${interactiveClass} text-left w-full`}
      >
        {inner}
      </button>
    );
  }
  // No destination — render as non-interactive so we don't trick the visitor.
  return (
    <article data-testid={testId} className={baseClass}>
      {inner}
    </article>
  );
}
